import type { PaymentAttemptState } from '../types'

/**
 * The `error_code`s api2 answers a /pay intent or charge with when a bill's payment
 * attempt is already under way (PaymentAttemptException). Every other failure is an
 * ordinary error the page shows as one.
 */
const STATE_BY_ERROR_CODE: Readonly<Record<string, PaymentAttemptState>> = {
  payment_processing: 'processing',
  payment_succeeded: 'received',
  payment_unresolved: 'unresolved',
  payment_in_progress: 'in_progress',
}

/** The shape `req` throws for a 4xx: the response body, plus the HTTP status. */
interface ApiErrorLike {
  error_code?: unknown
  status?: unknown
  statusCode?: unknown
  status_message?: unknown
}

const asApiError = (error: unknown): ApiErrorLike | null =>
  error !== null && typeof error === 'object' ? (error as ApiErrorLike) : null

/**
 * The attempt state a refused intent or charge proves, or null for an ordinary error.
 *
 * A SportsPay charge whose outcome the server could not learn answers 406 (no code):
 * the money may have moved, so it reads as `unresolved` — never as a failure to retry.
 */
export const paymentAttemptStateOf = (
  error: unknown
): PaymentAttemptState | null => {
  const apiError = asApiError(error)
  if (apiError === null) {
    return null
  }

  if (typeof apiError.error_code === 'string') {
    return STATE_BY_ERROR_CODE[apiError.error_code] ?? null
  }

  return apiError.status === 406 || apiError.statusCode === 406
    ? 'unresolved'
    : null
}

/**
 * The line to show under the card form when SportsPay DECLINED the card — its answer
 * carries the gateway's own text (`status_message`) and no `message`. Null for anything
 * that is not such a decline.
 */
export const declinedCardMessage = (error: unknown): string | null => {
  const text = asApiError(error)?.status_message

  return typeof text === 'string' && text.trim() !== ''
    ? `Your card was declined (${text.trim()}). Check the details or try another card.`
    : null
}

/**
 * A refusal worth another try from the same page (Sep 24): the provider did not answer
 * (`payment_retry` — the attempt is kept, and the next request replays it) or refused to
 * start the payment at all (`payment_refused` — the bill's claim was released).
 */
export const isRetryablePaymentError = (error: unknown): boolean => {
  const code = asApiError(error)?.error_code

  return code === 'payment_retry' || code === 'payment_refused'
}
